import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import type { ConversationsOutcomeMetric } from "../transport"
import {
  MIN_METRIC_ABSOLUTE_DELTA,
  MIN_METRIC_MOVEMENT_COUNT,
  MIN_METRIC_RELATIVE_DELTA,
  MIN_NEGATIVE_STRING_METRIC_COUNT,
  MIN_NEGATIVE_STRING_METRIC_SHARE,
} from "./conversationsSummaryConstants"
import type {
  ConversationsPageSummaryLookups,
  CountEntry,
} from "./conversationsSummaryTypes"
import {
  isMetricIssueStatus,
  normalizeStatus,
} from "./conversationsSummaryWindow"

export interface MetricMovement {
  id: string
  label: string
  currentAverage: number
  previousAverage: number
  currentCount: number
  previousCount: number
  relativeDelta: number
  isExact: boolean
}

export interface StringMetricSignal {
  id: string
  label: string
  totalCount: number
  negativeCount: number
  topNegativeValue: string
}

export function incrementCount(
  map: Map<string, CountEntry>,
  id: string,
  label = id,
): void {
  const current = map.get(id)
  map.set(id, {
    id,
    label: current?.label ?? label,
    count: (current?.count ?? 0) + 1,
  })
}

export function sortCountEntries(map: Map<string, CountEntry>): CountEntry[] {
  return Array.from(map.values()).sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label),
  )
}

export function getMetricLabel(
  metricId: string,
  lookups: ConversationsPageSummaryLookups | undefined,
): string {
  const metricRecord = lookups?.metricRecordsById?.[metricId]
  return (
    lookups?.metricDisplayNames?.[metricId] ??
    metricRecord?.metric_display_name ??
    metricRecord?.metric_name ??
    metricId
  )
}

export function getNumericMetricMovements(
  currentRows: ConversationsTableResult[],
  previousRows: ConversationsTableResult[],
  lookups: ConversationsPageSummaryLookups | undefined,
): MetricMovement[] {
  const currentValues = new Map<string, number[]>()
  const previousValues = new Map<string, number[]>()

  const addValues = (
    rows: ConversationsTableResult[],
    target: Map<string, number[]>,
  ) => {
    rows.forEach((row) => {
      Object.entries(row.metrics ?? {}).forEach(([metricId, value]) => {
        if (typeof value !== "number" || !Number.isFinite(value)) {
          return
        }
        const values = target.get(metricId) ?? []
        values.push(value)
        target.set(metricId, values)
      })
    })
  }

  addValues(currentRows, currentValues)
  addValues(previousRows, previousValues)

  const movements: MetricMovement[] = []
  currentValues.forEach((values, metricId) => {
    const previous = previousValues.get(metricId)
    if (
      values.length < MIN_METRIC_MOVEMENT_COUNT ||
      !previous ||
      previous.length < MIN_METRIC_MOVEMENT_COUNT
    ) {
      return
    }

    const currentAverage =
      values.reduce((sum, value) => sum + value, 0) / values.length
    const previousAverage =
      previous.reduce((sum, value) => sum + value, 0) / previous.length
    const absoluteDelta = Math.abs(currentAverage - previousAverage)
    const relativeDelta =
      Math.abs(previousAverage) < MIN_METRIC_ABSOLUTE_DELTA
        ? absoluteDelta
        : absoluteDelta / Math.abs(previousAverage)

    if (
      absoluteDelta < MIN_METRIC_ABSOLUTE_DELTA ||
      relativeDelta < MIN_METRIC_RELATIVE_DELTA
    ) {
      return
    }

    movements.push({
      id: metricId,
      label: getMetricLabel(metricId, lookups),
      currentAverage,
      previousAverage,
      currentCount: values.length,
      previousCount: previous.length,
      relativeDelta,
      isExact: false,
    })
  })

  return movements.sort(
    (a, b) =>
      b.relativeDelta - a.relativeDelta || a.label.localeCompare(b.label),
  )
}

export function getOutcomeMetricMovements(
  metrics: ConversationsOutcomeMetric[],
  lookups: ConversationsPageSummaryLookups | undefined,
): MetricMovement[] {
  return metrics
    .flatMap((metric): MetricMovement[] => {
      if (
        metric.currentAverage === null ||
        metric.previousAverage === null ||
        metric.currentCount < MIN_METRIC_MOVEMENT_COUNT ||
        metric.previousCount < MIN_METRIC_MOVEMENT_COUNT
      ) {
        return []
      }

      const absoluteDelta = Math.abs(
        metric.currentAverage - metric.previousAverage,
      )
      const relativeDelta =
        Math.abs(metric.previousAverage) < MIN_METRIC_ABSOLUTE_DELTA
          ? absoluteDelta
          : absoluteDelta / Math.abs(metric.previousAverage)
      if (
        absoluteDelta < MIN_METRIC_ABSOLUTE_DELTA ||
        relativeDelta < MIN_METRIC_RELATIVE_DELTA
      ) {
        return []
      }

      return [
        {
          id: metric.metricId,
          label: getMetricLabel(metric.metricId, lookups),
          currentAverage: metric.currentAverage,
          previousAverage: metric.previousAverage,
          currentCount: metric.currentCount,
          previousCount: metric.previousCount,
          relativeDelta,
          isExact: true,
        },
      ]
    })
    .sort(
      (a, b) =>
        b.relativeDelta - a.relativeDelta || a.label.localeCompare(b.label),
    )
}

export function getMetricIssueCounts(
  rows: ConversationsTableResult[],
  lookups: ConversationsPageSummaryLookups | undefined,
): CountEntry[] {
  const counts = new Map<string, CountEntry>()

  rows.forEach((row) => {
    Object.entries(row.metric_error_statuses ?? {}).forEach(
      ([metricId, status]) => {
        if (!isMetricIssueStatus(status)) {
          return
        }
        incrementCount(
          counts,
          `${metricId}:${normalizeStatus(status)}`,
          `${getMetricLabel(metricId, lookups)} ${normalizeStatus(status)}`,
        )
      },
    )
  })

  return sortCountEntries(counts)
}

function isNegativeStringMetricValue(value: string): boolean {
  const normalized = value.trim().toUpperCase()
  return (
    normalized === "NO" ||
    normalized === "FALSE" ||
    normalized === "FAIL" ||
    normalized === "FAILED" ||
    normalized === "ERROR" ||
    normalized === "UNSUCCESSFUL" ||
    normalized === "NOT_MET" ||
    normalized === "NOT MET"
  )
}

export function getStringMetricSignals(
  rows: ConversationsTableResult[],
  lookups: ConversationsPageSummaryLookups | undefined,
): StringMetricSignal[] {
  const totals = new Map<string, number>()
  const negativeCountsByMetric = new Map<string, Map<string, number>>()

  rows.forEach((row) => {
    Object.entries(row.metrics ?? {}).forEach(([metricId, value]) => {
      if (typeof value !== "string") {
        return
      }
      totals.set(metricId, (totals.get(metricId) ?? 0) + 1)
      if (!isNegativeStringMetricValue(value)) {
        return
      }
      const counts =
        negativeCountsByMetric.get(metricId) ?? new Map<string, number>()
      counts.set(value, (counts.get(value) ?? 0) + 1)
      negativeCountsByMetric.set(metricId, counts)
    })
  })

  return Array.from(negativeCountsByMetric.entries())
    .map(([metricId, negativeCounts]) => {
      const sortedNegativeValues = Array.from(negativeCounts.entries()).sort(
        (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
      )
      const negativeCount = sortedNegativeValues.reduce(
        (sum, [, count]) => sum + count,
        0,
      )
      return {
        id: metricId,
        label: getMetricLabel(metricId, lookups),
        totalCount: totals.get(metricId) ?? negativeCount,
        negativeCount,
        topNegativeValue: sortedNegativeValues[0]?.[0] ?? "negative",
      }
    })
    .filter(
      (signal) =>
        signal.negativeCount >= MIN_NEGATIVE_STRING_METRIC_COUNT ||
        signal.negativeCount / Math.max(1, signal.totalCount) >=
          MIN_NEGATIVE_STRING_METRIC_SHARE,
    )
    .sort(
      (a, b) =>
        b.negativeCount - a.negativeCount || a.label.localeCompare(b.label),
    )
}
